import React, { useState, useEffect, useRef } from 'react';
import {
  Store,
  Save,
  Loader2,
  MapPin,
  Phone,
  Mail,
  FileText,
  Image,
  CreditCard,
  RefreshCw,
  AlertCircle,
  UtensilsCrossed,
  Coffee,
  Hash,
  RotateCcw,
  Monitor,
  Globe,
  Flame,
  X,
  Plus,
  Pencil,
  Check,
  Building2,
  Clock,
  Receipt,
  Upload,
  Trash2,
  Printer,
  Smartphone,
  ChefHat,
  Palette,
} from 'lucide-react';
import toast from 'react-hot-toast';
import branchService from '../../../services/branchService';
import uploadService from '../../../services/uploadService';
import { TIMEZONE_OPTIONS } from '../../../constants/timezones';
import { clearBranchCache, setBranchCache } from '../../../hooks/useBranch';

// Get full URL for logo (prepend API base URL if relative path)
const getLogoFullUrl = (logoUrl) => {
  if (!logoUrl) return null;
  // If already a full URL, return as is
  if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
    return logoUrl;
  }
  // Prepend API base URL for relative paths
  const apiBaseUrl = import.meta.env.VITE_API_URL || 'http://localhost:8081';
  return `${apiBaseUrl}${logoUrl}`;
};

/**
 * Branch Settings Component
 *
 * Professional view/edit mode for restaurant configuration:
 * - View mode: Clean read-only display of all settings
 * - Edit mode: Full editing capabilities with Save/Cancel
 */

// ============================================
// VIEW MODE COMPONENTS
// ============================================

// View Section Header
function ViewSectionHeader({ icon: Icon, title, iconBg = 'bg-teal-100', iconColor = 'text-teal-600' }) {
  return (
    <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100">
      <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center`}>
        <Icon size={16} className={iconColor} />
      </div>
      <h3 className="font-semibold text-slate-900">{title}</h3>
    </div>
  );
}

// View Field - displays label and value
function ViewField({ label, value, icon: Icon, emptyText = 'Not set', className = '' }) {
  const isEmpty = !value || (Array.isArray(value) && value.length === 0);

  return (
    <div className={`${className}`}>
      <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1.5">
        {Icon && <Icon size={12} className="text-slate-400" />}
        {label}
      </dt>
      <dd className={`text-sm ${isEmpty ? 'text-slate-400 italic' : 'text-slate-900'}`}>
        {isEmpty ? emptyText : value}
      </dd>
    </div>
  );
}

// Service Mode Badge (View)
function ServiceModeBadge({ mode, isDefault, icon: Icon, color }) {
  const colorClasses = {
    teal: 'bg-teal-50 text-teal-700 border-teal-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
  };

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border ${colorClasses[color]}`}>
      <Icon size={16} />
      <span className="font-medium text-sm">{mode === 'DINE_IN' ? 'Dine-In' : 'Counter'}</span>
      {isDefault && (
        <span className="text-[10px] font-bold px-1.5 py-0.5 bg-slate-800 text-white rounded">
          DEFAULT
        </span>
      )}
    </div>
  );
}

// ============================================
// EDIT MODE COMPONENTS
// ============================================

// Service Mode Toggle Component (Edit)
function ServiceModeToggle({ mode, isEnabled, isDefault, onChange, onSetDefault, icon: Icon, title, description, color }) {
  const colorClasses = {
    teal: {
      bg: isEnabled ? 'bg-teal-50 border-teal-300' : 'bg-white border-slate-200',
      icon: 'bg-teal-100 text-teal-600',
      badge: 'bg-teal-600',
    },
    amber: {
      bg: isEnabled ? 'bg-amber-50 border-amber-300' : 'bg-white border-slate-200',
      icon: 'bg-amber-100 text-amber-600',
      badge: 'bg-amber-600',
    },
  };

  const colors = colorClasses[color] || colorClasses.teal;

  return (
    <div className={`relative rounded-lg border-2 p-3 transition-all ${colors.bg}`}>
      {isDefault && (
        <div className={`absolute -top-2 right-3 px-2 py-0.5 ${colors.badge} text-white text-[9px] font-bold rounded-full`}>
          DEFAULT
        </div>
      )}
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg ${colors.icon} flex items-center justify-center flex-shrink-0`}>
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900 text-sm">{title}</p>
          <p className="text-xs text-slate-500">{description}</p>
        </div>
        <div className="flex items-center gap-2">
          {isEnabled && !isDefault && (
            <button
              onClick={() => onSetDefault(mode)}
              className="text-[10px] px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded font-medium transition-colors"
            >
              Set Default
            </button>
          )}
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={isEnabled}
              onChange={(e) => onChange(mode, e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-slate-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-600"></div>
          </label>
        </div>
      </div>
    </div>
  );
}

// Text Input Component
function TextInput({ value, onChange, placeholder, disabled, type = 'text', maxLength }) {
  return (
    <input
      type={type}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      maxLength={maxLength}
      className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50 transition-all disabled:bg-slate-50 disabled:text-slate-500"
    />
  );
}

// Textarea Component
function TextArea({ value, onChange, placeholder, disabled, rows = 3 }) {
  return (
    <textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      rows={rows}
      className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50 transition-all resize-none disabled:bg-slate-50 disabled:text-slate-500"
    />
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function BranchSettings({ renderActions }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [branch, setBranch] = useState(null);
  const logoInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    contact: '',
    email: '',
    gstNumber: '',
    fssaiNumber: '',
    logoUrl: '',
    upiId: '',
    thankYouMessage: '',
    addressId: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipcode: '',
    country: 'India',
    serviceModes: ['DINE_IN'],
    defaultServiceMode: 'DINE_IN',
    counterTokenPrefix: 'C',
    counterTokenDailyReset: true,
    hotItems: [],
    timezone: 'Asia/Kolkata',
    kotModes: ['PRINT'],
    billDeliveryMethods: ['PRINT'],
    posDesignTheme: 1,
  });
  const [originalData, setOriginalData] = useState(null);

  const formDataRef = useRef(formData);
  const branchRef = useRef(branch);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  useEffect(() => {
    branchRef.current = branch;
  }, [branch]);

  // Fetch branch data
  const fetchBranch = async () => {
    setLoading(true);
    try {
      const data = await branchService.getCurrentBranch();
      if (data) {
        setBranch(data);

        const address = data.addresses && data.addresses.length > 0 ? data.addresses[0] : {};

        let modes = ['DINE_IN'];
        if (data.serviceModes) {
          if (Array.isArray(data.serviceModes)) {
            modes = data.serviceModes;
          } else if (typeof data.serviceModes === 'string') {
            modes = data.serviceModes.split(',').map(m => m.trim()).filter(Boolean);
          }
        }
        if (modes.length === 0) modes = ['DINE_IN'];

        let hotItemsArray = [];
        if (data.hotItems) {
          hotItemsArray = data.hotItems.split(',').map(item => item.trim()).filter(Boolean);
        }

        // Parse KOT modes (KDS, PRINT)
        let kotModesArray = ['PRINT'];
        if (data.kotModes) {
          if (Array.isArray(data.kotModes)) {
            kotModesArray = data.kotModes;
          } else if (typeof data.kotModes === 'string') {
            kotModesArray = data.kotModes.split(',').map(m => m.trim()).filter(Boolean);
          }
        }
        if (kotModesArray.length === 0) kotModesArray = ['PRINT'];

        // Parse bill delivery methods (PRINT, EBILL)
        let billDeliveryMethodsArray = ['PRINT'];
        if (data.billDeliveryMethods) {
          if (Array.isArray(data.billDeliveryMethods)) {
            billDeliveryMethodsArray = data.billDeliveryMethods;
          } else if (typeof data.billDeliveryMethods === 'string') {
            billDeliveryMethodsArray = data.billDeliveryMethods.split(',').map(m => m.trim()).filter(Boolean);
          }
        }
        if (billDeliveryMethodsArray.length === 0) billDeliveryMethodsArray = ['PRINT'];

        const newFormData = {
          name: data.name || '',
          description: data.description || '',
          contact: data.contact || '',
          email: data.email || '',
          gstNumber: data.gstNumber || '',
          fssaiNumber: data.fssaiNumber || '',
          logoUrl: data.logoUrl || '',
          upiId: data.upiId || '',
          thankYouMessage: data.thankYouMessage || '',
          addressId: address.id || '',
          address1: address.address1 || '',
          address2: address.address2 || '',
          city: address.city || '',
          state: address.state || '',
          zipcode: address.zipcode || '',
          country: address.country || 'India',
          serviceModes: modes,
          defaultServiceMode: data.defaultServiceMode || modes[0],
          counterTokenPrefix: data.counterTokenPrefix || 'C',
          counterTokenDailyReset: data.counterTokenDailyReset !== false,
          hotItems: hotItemsArray,
          timezone: data.timezone || 'Asia/Kolkata',
          kotModes: kotModesArray,
          billDeliveryMethods: billDeliveryMethodsArray,
          posDesignTheme: Number(data.posDesignTheme) === 2 ? 2 : 1,
        };

        setFormData(newFormData);
        setOriginalData(newFormData);
      }
    } catch (error) {
      console.error('Failed to fetch branch:', error);
      toast.error('Failed to load branch information');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranch();
  }, []);

  // Update form field
  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Toggle service mode
  const toggleServiceMode = (mode, enabled) => {
    setFormData(prev => {
      let newModes = [...prev.serviceModes];

      if (enabled) {
        if (!newModes.includes(mode)) {
          newModes.push(mode);
        }
      } else {
        newModes = newModes.filter(m => m !== mode);
        if (newModes.length === 0) {
          toast.error('At least one service mode must be enabled');
          return prev;
        }
      }

      let newDefault = prev.defaultServiceMode;
      if (!newModes.includes(newDefault)) {
        newDefault = newModes[0];
      }

      return {
        ...prev,
        serviceModes: newModes,
        defaultServiceMode: newDefault,
      };
    });
  };

  // Set default mode
  const setDefaultMode = (mode) => {
    setFormData(prev => ({
      ...prev,
      defaultServiceMode: mode,
    }));
  };

  // Handle logo file selection and upload
  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Client-side validation
    const validation = uploadService.validateFile(file, {
      allowedExtensions: ['png', 'jpg', 'jpeg', 'gif'],
      maxFileSize: 512000, // 500KB
    });

    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    setUploadingLogo(true);
    try {
      const result = await uploadService.uploadLogo(file);

      if (result?.success && result?.logoUrl) {
        updateField('logoUrl', result.logoUrl);
        toast.success('Logo uploaded successfully');
      } else {
        toast.error(result?.message || 'Failed to upload logo');
      }
    } catch (error) {
      console.error('Logo upload error:', error);
      toast.error('Failed to upload logo. Please try again.');
    } finally {
      setUploadingLogo(false);
      // Reset file input
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  // Handle logo removal
  const handleLogoRemove = () => {
    updateField('logoUrl', '');
    toast.success('Logo removed. Remember to save changes.');
  };

  // Enter edit mode
  const handleEdit = () => {
    setOriginalData({ ...formData });
    setIsEditing(true);
  };

  // Cancel editing
  const handleCancel = () => {
    if (originalData) {
      setFormData(originalData);
    }
    setIsEditing(false);
  };

  // Save changes
  const handleSave = async () => {
    const currentBranch = branchRef.current;
    const currentFormData = formDataRef.current;

    if (!currentBranch?.id) {
      toast.error('No branch found to update');
      return;
    }

    // Validation
    if (!currentFormData.name.trim()) {
      toast.error('Restaurant name is required');
      return;
    }

    setSaving(true);
    try {
      const updatePayload = {
        name: currentFormData.name,
        description: currentFormData.description,
        contact: currentFormData.contact,
        email: currentFormData.email,
        gstNumber: currentFormData.gstNumber,
        fssaiNumber: currentFormData.fssaiNumber,
        logoUrl: currentFormData.logoUrl,
        upiId: currentFormData.upiId,
        thankYouMessage: currentFormData.thankYouMessage,
        organizationId: currentBranch.organizationId,
        addresses: currentFormData.addressId ? [{
          id: currentFormData.addressId,
          address1: currentFormData.address1,
          address2: currentFormData.address2,
          city: currentFormData.city,
          state: currentFormData.state,
          zipcode: currentFormData.zipcode,
          country: currentFormData.country,
        }] : [],
        serviceModes: currentFormData.serviceModes,
        defaultServiceMode: currentFormData.defaultServiceMode,
        counterTokenPrefix: currentFormData.counterTokenPrefix,
        counterTokenDailyReset: currentFormData.counterTokenDailyReset,
        hotItems: currentFormData.hotItems.join(','),
        timezone: currentFormData.timezone,
        kotModes: currentFormData.kotModes,
        billDeliveryMethods: currentFormData.billDeliveryMethods,
        posDesignTheme: currentFormData.posDesignTheme,
      };

      await branchService.updateBranch(currentBranch.id, updatePayload);
      setBranchCache({
        ...currentBranch,
        ...updatePayload,
        id: currentBranch.id,
        organizationId: currentBranch.organizationId,
      });
      toast.success('Branch settings saved successfully');
      setIsEditing(false);
      setOriginalData({ ...currentFormData });
      fetchBranch();
    } catch (error) {
      console.error('Failed to update branch:', error);
      toast.error('Failed to save branch settings');
    } finally {
      setSaving(false);
    }
  };

  // Render actions for parent header
  useEffect(() => {
    if (renderActions) {
      renderActions({
        // View/Edit mode state
        isEditing,
        onEdit: handleEdit,
        onCancel: handleCancel,
        onSave: handleSave,
        onRefresh: fetchBranch,
        saving,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saving, isEditing]);

  // Format address for display
  const formatAddress = () => {
    const parts = [
      formData.address1,
      formData.address2,
      formData.city,
      formData.state,
      formData.zipcode,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  // Get timezone label
  const getTimezoneLabel = () => {
    const tz = TIMEZONE_OPTIONS.find(t => t.value === formData.timezone);
    return tz ? `${tz.label} (${tz.offset})` : formData.timezone;
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">Loading branch information...</p>
        </div>
      </div>
    );
  }

  // No branch found
  if (!branch) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <AlertCircle size={48} className="text-amber-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Branch Found</h3>
          <p className="text-slate-600 mb-4">Unable to load branch information.</p>
          <button
            onClick={fetchBranch}
            className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 flex items-center gap-2 mx-auto"
          >
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // VIEW MODE
  // ============================================
  if (!isEditing) {
    return (
      <div className="p-6">
        {/* Restaurant Header Info */}
        <div className="flex items-center gap-4 mb-6 pb-6 border-b border-slate-200">
          {formData.logoUrl ? (
            <img
              src={getLogoFullUrl(formData.logoUrl)}
              alt="Logo"
              className="w-16 h-16 rounded-xl object-contain bg-slate-100 border border-slate-200 p-1"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          ) : (
            <div className="w-16 h-16 rounded-xl bg-teal-100 flex items-center justify-center">
              <Store size={32} className="text-teal-600" />
            </div>
          )}
          <div>
            <h2 className="text-xl font-bold text-slate-900">{formData.name || 'Restaurant Name'}</h2>
            {formData.description && (
              <p className="text-sm text-slate-500 mt-0.5">{formData.description}</p>
            )}
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEFT COLUMN */}
          <div className="space-y-6">
            {/* Contact Information */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <ViewSectionHeader icon={Phone} title="Contact Information" />
              <dl className="grid grid-cols-2 gap-4">
                <ViewField label="Phone" value={formData.contact} icon={Phone} />
                <ViewField label="Email" value={formData.email} icon={Mail} />
              </dl>
            </div>

            {/* Address */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <ViewSectionHeader icon={MapPin} title="Location" />
              <dl className="space-y-3">
                <ViewField label="Address" value={formatAddress()} />
                <div className="grid grid-cols-2 gap-4">
                  <ViewField label="City" value={formData.city} />
                  <ViewField label="State" value={formData.state} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <ViewField label="PIN Code" value={formData.zipcode} />
                  <ViewField label="Country" value={formData.country} />
                </div>
              </dl>
            </div>

            {/* Tax & Compliance */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <ViewSectionHeader icon={FileText} title="Tax & Compliance" />
              <dl className="grid grid-cols-2 gap-4">
                <ViewField label="GSTIN" value={formData.gstNumber} emptyText="Not registered" />
                <ViewField label="FSSAI License" value={formData.fssaiNumber} emptyText="Not registered" />
              </dl>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-6">
            {/* Service Modes */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <ViewSectionHeader icon={Monitor} title="POS Service Modes" />
              <div className="flex flex-wrap gap-3">
                {formData.serviceModes.includes('DINE_IN') && (
                  <ServiceModeBadge
                    mode="DINE_IN"
                    isDefault={formData.defaultServiceMode === 'DINE_IN'}
                    icon={UtensilsCrossed}
                    color="teal"
                  />
                )}
                {formData.serviceModes.includes('COUNTER') && (
                  <ServiceModeBadge
                    mode="COUNTER"
                    isDefault={formData.defaultServiceMode === 'COUNTER'}
                    icon={Coffee}
                    color="amber"
                  />
                )}
              </div>
              {formData.serviceModes.includes('COUNTER') && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Counter Settings</p>
                  <div className="flex items-center gap-6 text-sm text-slate-700">
                    <span className="flex items-center gap-2">
                      <Hash size={14} className="text-slate-400" />
                      Token Prefix: <strong>{formData.counterTokenPrefix}</strong>
                    </span>
                    <span className="flex items-center gap-2">
                      <RotateCcw size={14} className="text-slate-400" />
                      Daily Reset: <strong>{formData.counterTokenDailyReset ? 'Yes' : 'No'}</strong>
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* POS Design Theme */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <ViewSectionHeader icon={Palette} title="POS Design Theme" iconBg="bg-indigo-100" iconColor="text-indigo-600" />
              <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-indigo-50 text-indigo-700 border-indigo-200">
                <Palette size={16} />
                <span className="font-medium text-sm">
                  {formData.posDesignTheme === 2 ? 'Theme 2' : 'Default Theme'}
                </span>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Controls the visual style of the POS table-selection screen.
              </p>
            </div>

            {/* KOT Mode */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <ViewSectionHeader icon={ChefHat} title="KOT Mode" iconBg="bg-orange-100" iconColor="text-orange-600" />
              <div className="flex flex-wrap gap-3">
                {formData.kotModes.includes('KDS') && (
                  <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-purple-50 text-purple-700 border-purple-200">
                    <Monitor size={16} />
                    <span className="font-medium text-sm">KDS (Kitchen Display)</span>
                  </div>
                )}
                {formData.kotModes.includes('PRINT') && (
                  <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-blue-50 text-blue-700 border-blue-200">
                    <Printer size={16} />
                    <span className="font-medium text-sm">Print Receipt</span>
                  </div>
                )}
              </div>
            </div>

            {/* Bill Delivery Method */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <ViewSectionHeader icon={Receipt} title="Bill Delivery Method" iconBg="bg-green-100" iconColor="text-green-600" />
              <div className="flex flex-wrap gap-3">
                {formData.billDeliveryMethods.includes('PRINT') && (
                  <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-blue-50 text-blue-700 border-blue-200">
                    <Printer size={16} />
                    <span className="font-medium text-sm">Print Receipt</span>
                  </div>
                )}
                {formData.billDeliveryMethods.includes('EBILL') && (
                  <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border bg-emerald-50 text-emerald-700 border-emerald-200">
                    <Smartphone size={16} />
                    <span className="font-medium text-sm">E-Bill (WhatsApp/SMS)</span>
                  </div>
                )}
              </div>
            </div>

            {/* Payment */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <ViewSectionHeader icon={CreditCard} title="Payment" />
              <ViewField label="UPI ID" value={formData.upiId} emptyText="Not configured" />
            </div>

            {/* Display & Localization */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <ViewSectionHeader icon={Globe} title="Display & Localization" />
              <dl className="space-y-4">
                <ViewField label="Timezone" value={getTimezoneLabel()} icon={Clock} />
                <div>
                  <dt className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <Flame size={12} className="text-orange-500" />
                    Specialty Items
                  </dt>
                  <dd>
                    {formData.hotItems.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {formData.hotItems.map((item, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-lg"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-sm text-slate-400 italic">No specialty items set</span>
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================
  // EDIT MODE
  // ============================================
  return (
    <div className="p-6">
      {/* Edit Mode Info Banner */}
      <div className="flex items-center gap-3 mb-6 pb-6 border-b border-slate-200">
        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
          <Pencil size={20} className="text-amber-600" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-900">Editing Branch Settings</h2>
          <p className="text-sm text-slate-500">Make changes and click Save in the header when done</p>
        </div>
      </div>

      {/* Edit Form Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT COLUMN */}
        <div className="space-y-6">
          {/* Restaurant Details */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100">
              <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                <Store size={16} className="text-teal-600" />
              </div>
              <h3 className="font-semibold text-slate-900">Restaurant Details</h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Restaurant Name <span className="text-red-500">*</span>
                </label>
                <TextInput
                  value={formData.name}
                  onChange={(v) => updateField('name', v)}
                  placeholder="Enter restaurant name"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Description</label>
                <TextArea
                  value={formData.description}
                  onChange={(v) => updateField('description', v)}
                  placeholder="Brief description of your restaurant"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Phone</label>
                  <TextInput
                    value={formData.contact}
                    onChange={(v) => updateField('contact', v)}
                    placeholder="+91 98765 43210"
                    type="tel"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Email</label>
                  <TextInput
                    value={formData.email}
                    onChange={(v) => updateField('email', v)}
                    placeholder="email@example.com"
                    type="email"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100">
              <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                <MapPin size={16} className="text-teal-600" />
              </div>
              <h3 className="font-semibold text-slate-900">Address</h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Street Address</label>
                <TextInput
                  value={formData.address1}
                  onChange={(v) => updateField('address1', v)}
                  placeholder="Street address, building name"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Address Line 2</label>
                <TextInput
                  value={formData.address2}
                  onChange={(v) => updateField('address2', v)}
                  placeholder="Area, landmark (optional)"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">City</label>
                  <TextInput
                    value={formData.city}
                    onChange={(v) => updateField('city', v)}
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">State</label>
                  <TextInput
                    value={formData.state}
                    onChange={(v) => updateField('state', v)}
                    placeholder="State"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">PIN Code</label>
                  <TextInput
                    value={formData.zipcode}
                    onChange={(v) => updateField('zipcode', v)}
                    placeholder="560001"
                    maxLength={6}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1.5">Country</label>
                  <TextInput
                    value={formData.country}
                    onChange={(v) => updateField('country', v)}
                    placeholder="India"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Tax & Compliance */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100">
              <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                <FileText size={16} className="text-teal-600" />
              </div>
              <h3 className="font-semibold text-slate-900">Tax & Compliance</h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">GSTIN</label>
                <TextInput
                  value={formData.gstNumber}
                  onChange={(v) => updateField('gstNumber', v)}
                  placeholder="22AAAAA0000A1Z5"
                  maxLength={15}
                />
                <p className="text-[10px] text-slate-400 mt-1">15-digit GST number</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">FSSAI License</label>
                <TextInput
                  value={formData.fssaiNumber}
                  onChange={(v) => updateField('fssaiNumber', v)}
                  placeholder="12345678901234"
                  maxLength={14}
                />
                <p className="text-[10px] text-slate-400 mt-1">14-digit FSSAI number</p>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-6">
          {/* Service Modes */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100">
              <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                <Monitor size={16} className="text-teal-600" />
              </div>
              <h3 className="font-semibold text-slate-900">POS Service Modes</h3>
            </div>

            <p className="text-xs text-slate-500 mb-4">
              Enable the service modes your business needs. The default mode opens automatically when entering POS.
            </p>

            <div className="space-y-3">
              <ServiceModeToggle
                mode="DINE_IN"
                isEnabled={formData.serviceModes.includes('DINE_IN')}
                isDefault={formData.defaultServiceMode === 'DINE_IN'}
                onChange={toggleServiceMode}
                onSetDefault={setDefaultMode}
                icon={UtensilsCrossed}
                title="Dine-In"
                description="Table service with KOT workflow"
                color="teal"
              />

              <ServiceModeToggle
                mode="COUNTER"
                isEnabled={formData.serviceModes.includes('COUNTER')}
                isDefault={formData.defaultServiceMode === 'COUNTER'}
                onChange={toggleServiceMode}
                onSetDefault={setDefaultMode}
                icon={Coffee}
                title="Counter"
                description="Quick service with token numbers"
                color="amber"
              />

              {formData.serviceModes.includes('COUNTER') && (
                <div className="ml-12 pl-3 border-l-2 border-amber-200 space-y-3 pt-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Hash size={14} className="text-slate-400" />
                      <span className="text-xs text-slate-600">Token Prefix</span>
                    </div>
                    <input
                      type="text"
                      value={formData.counterTokenPrefix}
                      onChange={(e) => updateField('counterTokenPrefix', e.target.value.toUpperCase())}
                      maxLength={3}
                      className="w-16 px-2 py-1.5 text-center text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-teal-400"
                      placeholder="C"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <RotateCcw size={14} className="text-slate-400" />
                      <span className="text-xs text-slate-600">Reset tokens daily</span>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.counterTokenDailyReset}
                        onChange={(e) => updateField('counterTokenDailyReset', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-600"></div>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* POS Design Theme */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100">
              <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Palette size={16} className="text-indigo-600" />
              </div>
              <h3 className="font-semibold text-slate-900">POS Design Theme</h3>
            </div>

            <p className="text-xs text-slate-500 mb-4">
              Choose the visual style for the POS table-selection screen.
            </p>

            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 1, title: 'Default', description: 'Classic POS layout' },
                { value: 2, title: 'Theme 2', description: 'New table design' },
              ].map((opt) => {
                const selected = formData.posDesignTheme === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateField('posDesignTheme', opt.value)}
                    className={`text-left rounded-lg border-2 p-3 transition-all ${
                      selected
                        ? 'bg-indigo-50 border-indigo-400'
                        : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        selected ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500'
                      }`}>
                        <Palette size={16} />
                      </div>
                      <span className={`text-sm font-semibold ${selected ? 'text-indigo-700' : 'text-slate-900'}`}>
                        {opt.title}
                      </span>
                      {selected && (
                        <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 bg-indigo-600 text-white rounded">
                          ACTIVE
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-slate-500">{opt.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* KOT Mode */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100">
              <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                <ChefHat size={16} className="text-orange-600" />
              </div>
              <h3 className="font-semibold text-slate-900">KOT Mode</h3>
            </div>

            <p className="text-xs text-slate-500 mb-4">
              Select how kitchen orders (KOT) should be sent. You can enable both options.
            </p>

            <div className="space-y-3">
              {/* KDS Option */}
              <label className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Monitor size={16} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">KDS (Kitchen Display)</p>
                    <p className="text-xs text-slate-500">Send orders to kitchen display system</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={formData.kotModes.includes('KDS')}
                  onChange={(e) => {
                    const newModes = e.target.checked
                      ? [...formData.kotModes, 'KDS']
                      : formData.kotModes.filter(m => m !== 'KDS');
                    if (newModes.length === 0) {
                      toast.error('At least one KOT mode must be enabled');
                      return;
                    }
                    updateField('kotModes', newModes);
                  }}
                  className="w-4 h-4 text-teal-600 rounded border-slate-300 focus:ring-teal-500"
                />
              </label>

              {/* Print Receipt Option */}
              <label className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Printer size={16} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Print Receipt</p>
                    <p className="text-xs text-slate-500">Print KOT on kitchen printer</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={formData.kotModes.includes('PRINT')}
                  onChange={(e) => {
                    const newModes = e.target.checked
                      ? [...formData.kotModes, 'PRINT']
                      : formData.kotModes.filter(m => m !== 'PRINT');
                    if (newModes.length === 0) {
                      toast.error('At least one KOT mode must be enabled');
                      return;
                    }
                    updateField('kotModes', newModes);
                  }}
                  className="w-4 h-4 text-teal-600 rounded border-slate-300 focus:ring-teal-500"
                />
              </label>
            </div>
          </div>

          {/* Bill Delivery Method */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100">
              <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                <Receipt size={16} className="text-green-600" />
              </div>
              <h3 className="font-semibold text-slate-900">Bill Delivery Method</h3>
            </div>

            <p className="text-xs text-slate-500 mb-4">
              Select how bills should be delivered to customers. You can enable both options.
            </p>

            <div className="space-y-3">
              {/* Print Receipt Option */}
              <label className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Printer size={16} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">Print Receipt</p>
                    <p className="text-xs text-slate-500">Print physical bill receipt</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={formData.billDeliveryMethods.includes('PRINT')}
                  onChange={(e) => {
                    const newMethods = e.target.checked
                      ? [...formData.billDeliveryMethods, 'PRINT']
                      : formData.billDeliveryMethods.filter(m => m !== 'PRINT');
                    if (newMethods.length === 0) {
                      toast.error('At least one bill delivery method must be enabled');
                      return;
                    }
                    updateField('billDeliveryMethods', newMethods);
                  }}
                  className="w-4 h-4 text-teal-600 rounded border-slate-300 focus:ring-teal-500"
                />
              </label>

              {/* E-Bill Option */}
              <label className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Smartphone size={16} className="text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">E-Bill (WhatsApp/SMS)</p>
                    <p className="text-xs text-slate-500">Send digital bill to customer's phone</p>
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={formData.billDeliveryMethods.includes('EBILL')}
                  onChange={(e) => {
                    const newMethods = e.target.checked
                      ? [...formData.billDeliveryMethods, 'EBILL']
                      : formData.billDeliveryMethods.filter(m => m !== 'EBILL');
                    if (newMethods.length === 0) {
                      toast.error('At least one bill delivery method must be enabled');
                      return;
                    }
                    updateField('billDeliveryMethods', newMethods);
                  }}
                  className="w-4 h-4 text-teal-600 rounded border-slate-300 focus:ring-teal-500"
                />
              </label>
            </div>
          </div>

          {/* Payment */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100">
              <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                <CreditCard size={16} className="text-teal-600" />
              </div>
              <h3 className="font-semibold text-slate-900">Payment</h3>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">UPI ID</label>
              <TextInput
                value={formData.upiId}
                onChange={(v) => updateField('upiId', v)}
                placeholder="restaurant@upi"
              />
              <p className="text-[10px] text-slate-400 mt-1">For QR code on receipts</p>
            </div>
          </div>

          {/* Branding */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100">
              <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                <Image size={16} className="text-teal-600" />
              </div>
              <h3 className="font-semibold text-slate-900">Branding</h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Restaurant Logo</label>
                <div className="flex items-start gap-4">
                  {/* Logo Preview */}
                  <div className="w-24 h-24 bg-slate-100 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden">
                    {formData.logoUrl ? (
                      <img
                        src={getLogoFullUrl(formData.logoUrl)}
                        alt="Logo"
                        className="max-h-20 max-w-20 object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : (
                      <div className="text-center text-slate-400">
                        <Image size={24} className="mx-auto mb-1" />
                        <span className="text-[10px]">No logo</span>
                      </div>
                    )}
                  </div>

                  {/* Upload Controls */}
                  <div className="flex-1 space-y-2">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/gif"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload"
                    />

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {uploadingLogo ? (
                          <>
                            <Loader2 size={14} className="animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload size={14} />
                            Upload Logo
                          </>
                        )}
                      </button>

                      {formData.logoUrl && (
                        <button
                          type="button"
                          onClick={handleLogoRemove}
                          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 text-red-600 transition-colors"
                        >
                          <Trash2 size={14} />
                          Remove
                        </button>
                      )}
                    </div>

                    <p className="text-[10px] text-slate-400">
                      PNG, JPG or GIF. Max 500KB. Recommended: 200x200px
                    </p>
                    <p className="text-[10px] text-slate-400">
                      Displayed on receipts and printed bills
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Display & Localization */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-100">
              <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                <Globe size={16} className="text-teal-600" />
              </div>
              <h3 className="font-semibold text-slate-900">Display & Localization</h3>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">Timezone</label>
                <select
                  value={formData.timezone}
                  onChange={(e) => updateField('timezone', e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50 transition-all"
                >
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label} ({tz.offset})
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-400 mt-1">Used for date/time calculations in reports</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  <span className="flex items-center gap-1">
                    <Flame size={12} className="text-orange-500" />
                    Specialty Items (Header Tags)
                  </span>
                </label>

                <div className="flex flex-wrap gap-2 mb-2">
                  {formData.hotItems.map((item, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-lg"
                    >
                      {item}
                      <button
                        type="button"
                        onClick={() => {
                          const newItems = formData.hotItems.filter((_, i) => i !== index);
                          updateField('hotItems', newItems);
                        }}
                        className="hover:text-orange-900"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>

                {formData.hotItems.length < 5 && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      id="newHotItem"
                      placeholder="Add specialty item (e.g., Chicken Biryani)"
                      className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50 transition-all"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const value = e.target.value.trim();
                          if (value && !formData.hotItems.includes(value)) {
                            updateField('hotItems', [...formData.hotItems, value]);
                            e.target.value = '';
                          }
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const input = document.getElementById('newHotItem');
                        const value = input.value.trim();
                        if (value && !formData.hotItems.includes(value)) {
                          updateField('hotItems', [...formData.hotItems, value]);
                          input.value = '';
                        }
                      }}
                      className="px-3 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                )}
                <p className="text-[10px] text-slate-400 mt-1">
                  {formData.hotItems.length}/5 items - Displayed as tags in POS header
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
